import { useNavigate } from 'react-router'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/features/offline/db'
import { HISTORY_LIMIT } from '@/features/favorites/store'
import { useApp } from '@/store/app'
import { Favoriten } from '@/components/Favoriten'
import { CardRow } from '@/components/CardRow'
import { Button, Empty, Label } from '@/components/primitives'
import { IconStar } from '@/components/icons'

/**
 * Startseite.
 *
 * Die Annahme: Wer die App im Einsatz öffnet, weiß meist schon, welche Karte er
 * braucht. Deshalb steht hier kein Werbeblock und keine Neuigkeitenliste, sondern die
 * selbst gewählten Favoriten — und sonst nichts, was zwischen den Daumen und die Karte
 * gerät.
 *
 * Ein Suchfeld stand hier früher auch. Es war eine Attrappe: angetippt führte sie nur
 * zur Suche. Seit die Suche als Knopf in der Navigationsleiste steht und von jeder
 * Ansicht aus erreichbar ist, kostete die Nachbildung nur Platz.
 */
export function Home() {
  const navigate = useNavigate()
  const catalog = useApp((s) => s.catalog)
  const offlineStates = useApp((s) => s.offlineStates)

  const favorites = useLiveQuery(() => db.favorites.orderBy('position').toArray(), [])
  const history = useLiveQuery(
    () => db.history.orderBy('openedAt').reverse().limit(HISTORY_LIMIT).toArray(),
    [],
  )

  if (!catalog) return null

  const byId = new Map(catalog.cards.map((card) => [card.id, card]))
  // flatMap statt map+filter: liefert direkt einen Typ ohne undefined, ohne Type-Guard.
  const favoriteCards = (favorites ?? []).flatMap((f) => byId.get(f.id) ?? [])
  const favoriteIds = new Set(favoriteCards.map((c) => c.id))
  // Favoriten stehen bereits oben — sie hier zu wiederholen wäre nur Rauschen.
  const historyCards = (history ?? []).flatMap((h) => {
    const card = byId.get(h.id)
    return card && !favoriteIds.has(card.id) ? [card] : []
  })

  const missing = catalog.cards.filter((c) => offlineStates.get(c.id) === 'fehlt').length
  const outdated = catalog.cards.filter((c) => offlineStates.get(c.id) === 'veraltet').length

  // Liegt noch keine einzige Karte auf dem Gerät, sagt die Schaltfläche darunter bereits
  // alles. Ein Satz, der dasselbe noch einmal erklärt, hält im Einsatz nur auf.
  const stand =
    missing === 0 && outdated === 0 ? (
      <>Alle {catalog.cards.length} Karten sind offline verfügbar.</>
    ) : outdated > 0 ? (
      <>
        <strong className="font-semibold text-ink">{outdated} Karten</strong> wurden
        aktualisiert
        {missing > 0 && <>, {missing} sind noch nicht geladen</>}.
      </>
    ) : missing === catalog.cards.length ? null : (
      <>
        <strong className="font-semibold text-ink">
          {catalog.cards.length - missing} von {catalog.cards.length}
        </strong>{' '}
        Karten sind offline verfügbar.
      </>
    )

  return (
    <div>
      {/* Das Logo steht in der Absenderleiste darüber (siehe AppShell). */}
      <header className="border-b border-line bg-bg-deep px-4 pb-3 pt-3">
        <div>
          <h1 className="text-[1.75rem] font-bold leading-tight">Taschenkarten</h1>
          <p className="mt-0.5 text-sm text-muted">
            Einsatzunterlagen — auch ohne Netzverbindung
          </p>
        </div>
      </header>

      <section className="px-4 py-5">
        {favoriteCards.length === 0 ? (
          <div>
            <Label>Favoriten</Label>
            <div className="mt-3">
            <Empty
              title="Noch keine Favoriten"
              hint="Karten, die Sie im Einsatz griffbereit brauchen, mit dem Stern markieren — sie erscheinen dann hier und werden automatisch offline vorgehalten."
              action={
                <Button variant="primary" onClick={() => void navigate('/katalog')}>
                  <IconStar className="size-5" />
                  Karten auswählen
                </Button>
              }
            />
            </div>
          </div>
        ) : (
          <Favoriten cards={favoriteCards} />
        )}
      </section>

      {historyCards.length > 0 && (
        <section className="px-4 pb-5">
          <Label>Zuletzt geöffnet</Label>
          <div className="mt-3 border-t border-line">
            {historyCards.map((card) => (
              <CardRow key={card.id} card={card} />
            ))}
          </div>
        </section>
      )}

      <section className="px-4 pb-8">
        <div className="rounded-card border border-line bg-surface px-4 py-3">
          {stand && <p className="text-sm text-muted">{stand}</p>}
          {(missing > 0 || outdated > 0) && (
            <div className={stand ? 'mt-3' : undefined}>
              <Button onClick={() => void navigate('/einstellungen')} full>
                Karten offline verfügbar machen
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
